/**
 * Store: Overlays
 * -----------------------------------------------------------------------------
 * Drawers, modals, and shelves.
 * - Tied to the overlay component.
 * - Only JSDoc custom parameters, ignore module state or object.
 *
 */

/**
 * Overlay object.
 * @typedef {Object} Overlay
 * @property {Boolean} [clearQueue] - Clears other overlays from queue.
 * @property {String|Array} [component] - Component handle, or array of
 * component handles to update state to `loading` when overlay opens, updated in
 * the index component.
 * @property {Object} [focusElements] - Additional focus trap elements.
 * @property {Array} [focusElements.containers] - Array of additional elements
 * to search inside for focusable elements for the focus trap.
 * @property {Array} [focusElements.elements] - Array of additional elements to
 * include in the focus trap.
 * @property {Boolean} [focusElements.prepend] - By default the order of
 * elements in the focus trap is overlay > containers > elements, to prepend the
 * containers and elements so the order of elements > containers > overlay then
 * set this to true.
 * @property {Boolean} [ignoreDismissed] - Open even if already dismissed.
 * @property {Number} [index] - Automatically generated, do not manually pass,
 * overlay's index in the active array, -1 if not in the array, passed to
 * mutations to keep them simple.
 * @property {String} namespace - Namespace of overlay.
 * @property {Boolean} [queue] - If the overlay should be queued.
 * @property {Boolean} [stuck] - If the overlay should be stuck when it is
 * active, meaning it cannot be closed until stuck is set to false.
 */
export default {
  namespaced: true,

  state: () => ({
    active: [],
    dismissed: [],
  }),

  getters: {

    /**
     * Get the current active overlay's object.
     * @returns {Object}
     */
    getActiveOverlay(state) {
      return state.active[0]
    },

    /**
     * Get overlay object by namespace or object.
     * @param {String|Object} arg - Overlay namespace or object.
     * @returns {Object}
     */
    getOverlay: (state) => (arg) => {
      const namespace = typeof arg === 'object' ? arg.namespace : arg
      return state.active.find((overlay) => overlay.namespace === namespace)
    },

    /**
     * Get if the overlay namespace has been dismissed.
     * - Returns false if ignoreDismissed is true.
     * - Or if theme settings have disabled overlays.
     * @param {Overlay} overlay - Overlay object.
     * @returns {Boolean}
     */
    getOverlayDismissed: (state) => (overlay) => {
      const matchesDismissed = state.dismissed.find((dismissed) => {
        return dismissed === overlay.namespace
      })

      return (
        matchesDismissed &&
        !overlay.ignoreDismissed &&
        !cnvs.settings.disableOverlaysDismissed
      )
    },

    /**
     * Get overlay index by namespace or object.
     * @param {String|Object} arg - Overlay namespace or object.
     * @returns {Number}
     */
    getOverlayIndex: (state) => (arg) => {
      const namespace = typeof arg === 'object' ? arg.namespace : arg
      return state.active.findIndex((overlay) => overlay.namespace === namespace)
    },
  },

  mutations: {

    /**
     * Closes overlay at active array index.
     * @param {Number} index - Index of overlay in active array to remove, if -1
     * then remove the first overlay in active array.
     */
    SET_CLOSE(state, index) {
      const mutateIndex = index > 0 ? index : 0
      state.active.splice(mutateIndex, 1)
    },

    /**
     * Closes all overlays.
     * - Clears the queue.
     */
    SET_CLOSE_ALL(state) {
      state.active = []
    },

    /**
     * Set dismissed state.
     * @param {String} namespace - Namespace to add to dismissed.
     */
    SET_DISMISSED(state, namespace) {
      if (!namespace || state.dismissed.includes(namespace)) {
        return
      }

      state.dismissed.push(namespace)
    },

    /**
     * Add overlay to active array.
     * @param {Overlay} overlay - Overlay object.
     */
    SET_OPEN(state, overlay) {
      if (overlay.clearQueue) {
        state.active = [overlay]
        return
      }

      /**
       * If existing overlay and queued then update.
       */
      if (overlay.index > -1 && overlay.queue) {
        state.active[overlay.index] = overlay
        return
      }

      /**
       * If not existing and queueing then add to end.
       */
      if (overlay.queue) {
        state.active.push(overlay)
        return
      }

      /**
       * Remove item if existing and not queueing.
       */
      if (overlay.index > -1) {
        state.active.splice(overlay.index, 1)
      }

      state.active.unshift(overlay)
    },

    /**
     * Set stuck state of overlay.
     * @param {Overlay} overlay - Overlay object.
     */
    SET_STUCK(state, overlay) {
      state.active[overlay.index] = overlay
    },
  },

  actions: {

    /**
     * Close overlay.
     * - Closes first active overlay if no namespace is provided.
     * - Does nothing if provided namespace does not match active overlays or
     *   active/provided overlay is stuck.
     * - This behaviour means that a queued overlay can be closed even if the
     *   active overlay is stuck.
     * @param {String} [namespace] - Overlay namespace to close.
     * @param {Boolean} [dismiss] - Add overlay to dismissed state.
     */
    close({ commit, getters, state }, namespace, dismiss = true) {
      const overlay = getters.getOverlay(namespace)

      if ((namespace && !overlay) || overlay.stuck) {
        return
      }

      const index = getters.getOverlayIndex(namespace)

      if (dismiss) {
        const dismissedOverlay = namespace
          ? state.active[index]
          : state.active[0]

        commit('SET_DISMISSED', dismissedOverlay?.namespace)
      }

      commit('SET_CLOSE', index)
    },

    /**
     * Closes all overlays.
     * - If active overlay is stuck then action is ignored.
     * - Only the current active overlay is added to dismissed.
     * @param {Boolean} [dismiss] - Add overlay to dismissed state.
     */
    closeAll({ commit, state }, dismiss = true) {
      if (state.active[0]?.stuck) {
        return
      }

      if (state.active.length && dismiss) {
        commit('SET_DISMISSED', state.active[0].namespace)
      }

      commit('SET_CLOSE_ALL')
    },

    /**
     * Open overlay.
     * - Only open if not dismissed, or ignoredDismissed is true.
     * - If active overlay is stuck then queue new overlay.
     * @param {Overlay} overlay - Overlay object.
     */
    open({ commit, getters }, overlay) {
      if (getters.getOverlayDismissed(overlay)) {
        return
      }

      const index = getters.getOverlayIndex(overlay)

      if (getters.getActiveOverlay?.stuck) {
        commit('SET_OPEN', { ...overlay, index, queue: true })
        return
      }

      commit('SET_OPEN', { ...overlay, index })
    },

    /**
     * Queues overlay.
     * @param {Overlay} overlay - Overlay object.
     */
    queue({ dispatch }, overlay) {
      dispatch('open', { ...overlay, queue: true })
    },

    /**
     * Set stuck state of overlay.
     * - Overlay namespace must already exist and be in active array.
     * @param {String} namespace - Overlay namespace.
     * @param {Boolean} stuck - If the overlay is stuck or not.
     */
    setStuck({ commit, getters }, { namespace, stuck }) {
      const index = getters.getOverlayIndex(namespace)

      if (index < 0) {
        return
      }

      const overlay = getters.getOverlay(namespace)
      commit('SET_STUCK', { ...overlay, index, stuck })
    },

    /**
     * Toggle overlay.
     * - Close if overlay is already active.
     * - Otherwise make overlay active.
     * @param {Overlay} overlay - Overlay object.
     */
    toggle({ dispatch, getters }, overlay) {
      if (getters.getOverlayIndex(overlay) > -1) {
        dispatch('close', overlay.namespace)
        return
      }

      dispatch('open', overlay)
    },
  },
}
